import {useCallback, useEffect, useMemo, useState} from 'react';

/**
 * The slice of a column definition that visibility toggling needs. Tables carry
 * their own extra per-column data (widths, labels, view-specific defaults); this
 * hook only cares which keys exist and which of them the user may hide.
 */
export interface VisibilityColumnDef<K extends string> {
	key: K;
	toggleable: boolean;
}

export interface PersistedColumnVisibilityConfig<K extends string> {
	/**
	 * localStorage key holding the user's choice. Must be distinct per table —
	 * two tables sharing a key would overwrite each other's visible set, and the
	 * keys of one would be discarded as unknown by the other.
	 */
	storageKey: string;
	/** Every column of the table, toggleable or not. */
	columns: readonly VisibilityColumnDef<K>[];
	/** The keys visible before the user has touched the columns menu. */
	defaultVisibleKeys: ReadonlySet<K>;
}

export interface ColumnVisibility<K extends string> {
	visible: ReadonlySet<K>;
	isVisible: (key: K) => boolean;
	toggle: (key: K) => void;
}

/**
 * Per-table column visibility, persisted to localStorage.
 *
 * Only *toggleable* keys are persisted. Non-toggleable columns are re-added from
 * the definitions on every load, so making a column mandatory later brings it
 * back for users whose stored blob predates the change — rather than leaving it
 * invisible for everyone who had already opened the menu.
 *
 * Unknown keys in the stored blob are dropped, so removing or renaming a column
 * degrades to "that column is gone" instead of throwing.
 */
export function usePersistedColumnVisibility<K extends string>({
	storageKey,
	columns,
	defaultVisibleKeys,
}: PersistedColumnVisibilityConfig<K>): ColumnVisibility<K> {
	const {toggleableKeys, alwaysOnKeys, allKeys} = useMemo(() => {
		const toggleable = new Set<string>();
		const alwaysOn = new Set<K>();
		const all = new Set<string>();
		for (const col of columns) {
			all.add(col.key);
			if (col.toggleable) toggleable.add(col.key);
			else alwaysOn.add(col.key);
		}
		return {toggleableKeys: toggleable, alwaysOnKeys: alwaysOn, allKeys: all};
	}, [columns]);

	const readPersisted = useCallback((): Set<K> | null => {
		if (typeof window === 'undefined') return null;
		try {
			const raw = window.localStorage.getItem(storageKey);
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			if (!Array.isArray(parsed)) return null;
			const next = new Set<K>(alwaysOnKeys);
			for (const key of parsed) {
				if (typeof key === 'string' && allKeys.has(key)) {
					next.add(key as K);
				}
			}
			return next;
		} catch {
			// Corrupt JSON or a disabled localStorage: fall back to the defaults
			// rather than leaving the table with no columns.
			return null;
		}
	}, [storageKey, alwaysOnKeys, allKeys]);

	const [visible, setVisible] = useState<Set<K>>(() => readPersisted() ?? new Set(defaultVisibleKeys));

	useEffect(() => {
		if (typeof window === 'undefined') return;
		try {
			const toggleableOn: K[] = [];
			for (const key of visible) {
				if (toggleableKeys.has(key)) toggleableOn.push(key);
			}
			window.localStorage.setItem(storageKey, JSON.stringify(toggleableOn));
		} catch {
			// localStorage may be disabled or quota-exceeded; silently ignore.
		}
	}, [visible, storageKey, toggleableKeys]);

	const toggle = useCallback(
		(key: K) => {
			if (!toggleableKeys.has(key)) return;
			setVisible(prev => {
				const next = new Set(prev);
				if (next.has(key)) next.delete(key);
				else next.add(key);
				return next;
			});
		},
		[toggleableKeys]
	);

	const isVisible = useCallback((key: K) => visible.has(key), [visible]);

	return useMemo(() => ({visible, isVisible, toggle}), [visible, isVisible, toggle]);
}
